/* eslint-disable no-param-reassign */
import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef
} from 'react';
import BotBuilderContext, { BotBuilderContextProps } from './BotBuilderContext';
import Intent from '../../types/BotBuilder/Intent';
import Slot from '../../types/BotBuilder/Slot';
import BotResponseTreeNode from '../../types/BotBuilder/BotResponseTreeNode';
import BotBuilderService from '../../services/BotBuilderService/BotBuilderService';
import { apiToDomain } from '../../pages/BotBuilderPage/utils/intentutils';
import ChatbotContext from '../ChatbotContext/ChatbotContext';
import IntentTreeNode from '../../types/BotBuilder/IntentTreeNode';
import MessageBlockTreeNode from '../../types/BotBuilder/MessageBlockTreeNode';
import CollectVariableTreeNode from '../../types/BotBuilder/CollectVariableTreeNode';
import ResponseCardsTreeNode from '../../types/BotBuilder/ResponseCardsTreeNode';
import IntentSlot from '../../types/BotBuilder/IntentSlot';
import ConditionalTreeNode from '../../types/BotBuilder/ConditionalTreeNode';
import GenerativeAiTreeNode from '../../types/BotBuilder/GenerativeAiTreeNode';
import CalculationTreeNode from '../../types/BotBuilder/CalculationTreeNode';
import SendEmailTreeNode from '../../types/BotBuilder/SendEmailTreeNode';
import SendSmsTreeNode from '../../types/BotBuilder/SendSmsTreeNode';
import DeleteVariableTreeNode from '../../types/BotBuilder/DeleteVariableTreeNode';
import CodeBlockTreeNode from '../../types/BotBuilder/CodeBlockTreeNode';
import LlmTreeNode from '../../types/BotBuilder/LlmTreeNode';
import SlotValueType from '../../types/BotBuilder/SlotValueType';
import SystemVariableService, {
  SystemVariableDto
} from '../../services/SystemVariableService/SystemVariableService';
import UserContext from '../UserContext/UserContext';

interface BotBuilderProviderProps {
  children: React.ReactNode;
}

const slotExistsByName = (slots: IntentSlot[], slotName: string): boolean =>
  slots.some((s) => s.slotName === slotName);

function BotBuilderProvider({
  children
}: BotBuilderProviderProps): React.ReactElement {
  const { currentChatbot } = useContext(ChatbotContext);
  const { user } = useContext(UserContext);
  const chatbotId = currentChatbot ? currentChatbot.id : null;

  const [intent, setIntent] = useState<Intent | null>(null);
  const [intentHasChanged, setIntentHasChanged] = useState<boolean>(false);

  const isInitializingRef = useRef<boolean>(false);
  const initializingIntentIdRef = useRef<number | null>(null);
  const initializationTimestampRef = useRef<number>(0);

  const [slots, setSlots] = useState<Slot[]>([]);
  const [systemVariables, setSystemVariables] = useState<SystemVariableDto[]>(
    []
  );

  const [unconnectedNodes, setUnconnectedNodes] = useState<
    BotResponseTreeNode[]
  >([]);
  const [previousIntentId, setPreviousIntentId] = useState<number | null>(null);

  useEffect(() => {
    if (intent?.id !== null && intent?.id !== previousIntentId) {
      if (previousIntentId !== null) {
        setUnconnectedNodes([]);
      }
      setPreviousIntentId(intent?.id || null);
    }
  }, [intent?.id, previousIntentId]);

  const addNode = useCallback((node: BotResponseTreeNode) => {
    setUnconnectedNodes((prev) => [...prev, node]);
  }, []);

  const removeNode = useCallback((node: BotResponseTreeNode) => {
    setUnconnectedNodes((prev) => prev.filter((n) => n.id !== node.id));
  }, []);

  const getIntent = useCallback(
    async (intentId: number) => {
      if (!chatbotId || !intentId) return;
      try {
        const response = await BotBuilderService.getIntent(chatbotId, intentId);
        const { intent: parsedIntent, unconnectedNodes: unconnected } =
          apiToDomain(response);
        setIntent(parsedIntent);
        setUnconnectedNodes(unconnected);
      } catch (error) {
        console.error('Failed to fetch intent:', error);
      }
    },
    [chatbotId]
  );

  const updateSlot = useCallback(
    async (slot: Slot, intentId?: number) => {
      if (!chatbotId) return;
      const updatedSlot = await BotBuilderService.updateSlot(chatbotId, slot);
      setSlots((prev) =>
        prev.map((s) => (s.id === updatedSlot.id ? updatedSlot : s))
      );

      if (intentId) {
        await getIntent(intentId);
      }
    },
    [chatbotId, getIntent]
  );

  const deleteSlot = useCallback(
    async (slotId: number, index: number, intentId?: number) => {
      if (!chatbotId) return;
      await BotBuilderService.deleteSlot(chatbotId, slotId);
      setSlots((prev) => {
        const newSlots = [...prev];
        newSlots.splice(index, 1);
        return newSlots;
      });

      if (intentId) {
        await getIntent(intentId);
      }
    },
    [chatbotId, getIntent]
  );

  const refreshSlots = useCallback(async () => {
    if (!chatbotId) return;
    try {
      const loadedSlots = await BotBuilderService.getSlots(chatbotId);
      setSlots(loadedSlots);
    } catch (error) {
      console.error('Failed to load slots', error);
    }
  }, [chatbotId]);

  const createSlot = useCallback(
    async (newSlot: Slot) => {
      if (!chatbotId) return;
      const createdSlot = await BotBuilderService.createSlot(
        chatbotId,
        newSlot
      );
      setSlots((prev) => [...prev, createdSlot]);
    },
    [chatbotId]
  );

  const onNodeUpdate = useCallback(
    (node: BotResponseTreeNode) => {
      if (!intent) return;

      const unconnectedNodeIndex = unconnectedNodes.findIndex(
        (unconnectedNode) => unconnectedNode.id === node.id
      );

      if (unconnectedNodeIndex !== -1) {
        const updatedUnconnectedNodes = [...unconnectedNodes];
        updatedUnconnectedNodes[unconnectedNodeIndex] = node;
        setUnconnectedNodes(updatedUnconnectedNodes);
        setIntentHasChanged(true);
        return;
      }

      const updatedIntent: Intent = { ...intent };
      const newSlots: IntentSlot[] = [];
      const existingSlotsByName = new Map(
        intent.slots.map((slot) => [slot.slotName, slot])
      );
      const nodes: BotResponseTreeNode[] = [
        updatedIntent.rootNode,
        ...unconnectedNodes
      ];

      const visitedNodeIds = new Set<string>();

      let matchingNode: BotResponseTreeNode | null = null;

      while (nodes.length > 0) {
        const currentNode = nodes.shift()!;

        if (visitedNodeIds.has(currentNode.id)) {
          // eslint-disable-next-line no-continue
          continue;
        }

        visitedNodeIds.add(currentNode.id);

        if (currentNode.id === node.id) {
          matchingNode = currentNode;
        }

        if (
          currentNode.content instanceof IntentTreeNode ||
          currentNode.content instanceof MessageBlockTreeNode ||
          currentNode.content instanceof GenerativeAiTreeNode ||
          currentNode.content instanceof CalculationTreeNode ||
          currentNode.content instanceof SendEmailTreeNode ||
          currentNode.content instanceof SendSmsTreeNode ||
          currentNode.content instanceof DeleteVariableTreeNode ||
          currentNode.content instanceof LlmTreeNode
        ) {
          const nodeContent = currentNode.content as
            | IntentTreeNode
            | MessageBlockTreeNode
            | GenerativeAiTreeNode
            | CalculationTreeNode
            | SendEmailTreeNode
            | SendSmsTreeNode
            | DeleteVariableTreeNode
            | LlmTreeNode;
          if (
            nodeContent.nextStep &&
            !visitedNodeIds.has(nodeContent.nextStep.id)
          ) {
            nodes.push(nodeContent.nextStep);
          }
        }

        if (currentNode.content instanceof CodeBlockTreeNode) {
          if (
            currentNode.content.nextStep &&
            !visitedNodeIds.has(currentNode.content.nextStep.id)
          ) {
            nodes.push(currentNode.content.nextStep);
          }
          if (
            currentNode.content.nextStepOnError &&
            !visitedNodeIds.has(currentNode.content.nextStepOnError.id)
          ) {
            nodes.push(currentNode.content.nextStepOnError);
          }
        }

        if (currentNode.content instanceof ResponseCardsTreeNode) {
          const nodeContent = currentNode.content as ResponseCardsTreeNode;
          nodeContent.items.forEach((item) => {
            if (item.nextStep && !visitedNodeIds.has(item.nextStep.id)) {
              nodes.push(item.nextStep);
            }
          });
        }

        if (currentNode.content instanceof ConditionalTreeNode) {
          const nodeContent = currentNode.content as ConditionalTreeNode;
          if (nodeContent.trueStep) {
            nodes.push(nodeContent.trueStep);
          }
          if (nodeContent.falseStep) {
            nodes.push(nodeContent.falseStep);
          }
        }

        if (currentNode.content instanceof CollectVariableTreeNode) {
          const nodeContent = currentNode.content as CollectVariableTreeNode;

          if (
            nodeContent.fallbackStep &&
            !visitedNodeIds.has(nodeContent.fallbackStep.id)
          ) {
            nodes.push(nodeContent.fallbackStep);
          }

          if (
            nodeContent.slotProvidedStep &&
            !visitedNodeIds.has(nodeContent.slotProvidedStep.id)
          ) {
            nodes.push(nodeContent.slotProvidedStep);
          }

          if (nodeContent.slotConditionals) {
            nodeContent.slotConditionals.forEach((cond) => {
              if (cond.nextStep && !visitedNodeIds.has(cond.nextStep.id)) {
                nodes.push(cond.nextStep);
              }
            });
          }

          if (nodeContent.slotName) {
            if (nodeContent.slotType) {
              // Global slots
              if (nodeContent.intentSlotId) {
                const existingSlot = existingSlotsByName.get(
                  nodeContent.slotName
                );
                newSlots.push({
                  ...existingSlot,
                  id: nodeContent.intentSlotId,
                  slotName: nodeContent.slotName,
                  slotType: nodeContent.slotType
                });
              } else {
                const exists = newSlots.find(
                  (s) =>
                    s.slotName === nodeContent.slotName &&
                    s.slotType === nodeContent.slotType
                );
                if (!exists) {
                  const existingSlot = existingSlotsByName.get(
                    nodeContent.slotName
                  );
                  newSlots.push({
                    ...existingSlot,
                    id: null,
                    slotName: nodeContent.slotName,
                    slotType: nodeContent.slotType
                  });
                }
              }
            } else {
              // Intent level slots
              const exists = newSlots.find(
                (s) =>
                  s.slotName === nodeContent.slotName && s.slotType === null
              );
              if (!exists) {
                const existingSlot = existingSlotsByName.get(
                  nodeContent.slotName
                );
                newSlots.push({
                  ...existingSlot,
                  id: null,
                  slotName: nodeContent.slotName,
                  slotType: null,
                  inlineValueType: nodeContent.slotValueType ?? undefined
                });
              }
            }
          }
        }

        if (currentNode.content instanceof CodeBlockTreeNode) {
          if (currentNode.content.outputMappings) {
            currentNode.content.outputMappings.forEach((mapping) => {
              if (mapping.intentVariable) {
                const exists = newSlots.find(
                  (s) => s.slotName === mapping.intentVariable
                );
                if (!exists) {
                  newSlots.push({
                    id: null,
                    slotName: mapping.intentVariable,
                    slotType: null,
                    inlineValueType: SlotValueType.FREE_TEXT
                  });
                }
              }
            });
          }
        }

        if (currentNode.content instanceof LlmTreeNode) {
          const { outputVariableName, outputValueType } = currentNode.content;
          if (outputVariableName) {
            const existingSlotIndex = newSlots.findIndex(
              (s) => s.slotName === outputVariableName
            );
            if (existingSlotIndex !== -1) {
              newSlots[existingSlotIndex] = {
                ...newSlots[existingSlotIndex],
                inlineValueType: outputValueType ?? SlotValueType.FREE_TEXT
              };
            } else {
              newSlots.push({
                id: null,
                slotName: outputVariableName,
                slotType: null,
                inlineValueType: outputValueType ?? SlotValueType.FREE_TEXT
              });
            }
          }
        }
      }

      updatedIntent.slots = newSlots;

      if (matchingNode) {
        matchingNode.content = node.content;
      }

      const isInInitializationPhase = isInitializingRef.current;
      const isInitializingThisIntent =
        initializingIntentIdRef.current === intent.id;
      const timeSinceInitialization =
        Date.now() - initializationTimestampRef.current;
      const isInGracePeriod =
        isInitializingThisIntent && timeSinceInitialization < 2000;

      if (isInInitializationPhase || isInGracePeriod) {
        return;
      }

      setIntent(updatedIntent);
      setIntentHasChanged(true);
    },
    [intent, unconnectedNodes, setUnconnectedNodes]
  );

  const onNodeDelete = useCallback(
    (node: BotResponseTreeNode) => {
      if (!intent) return;
      const updatedIntent: Intent = { ...intent };
      const nodes: BotResponseTreeNode[] = [updatedIntent.rootNode];
      const visitedNodeIds = new Set<string>();

      const traverseAndDelete = (currentNode: BotResponseTreeNode): void => {
        const { content } = currentNode;

        if (
          content instanceof IntentTreeNode ||
          content instanceof MessageBlockTreeNode ||
          content instanceof GenerativeAiTreeNode ||
          content instanceof CalculationTreeNode ||
          content instanceof SendEmailTreeNode ||
          content instanceof SendSmsTreeNode ||
          content instanceof DeleteVariableTreeNode ||
          content instanceof LlmTreeNode
        ) {
          if (content.nextStep?.id === node.id) {
            content.nextStep = null;
          } else if (
            content.nextStep &&
            !visitedNodeIds.has(content.nextStep.id)
          ) {
            nodes.push(content.nextStep);
          }
        }

        if (content instanceof CodeBlockTreeNode) {
          if (content.nextStep?.id === node.id) {
            content.nextStep = null;
          } else if (
            content.nextStep &&
            !visitedNodeIds.has(content.nextStep.id)
          ) {
            nodes.push(content.nextStep);
          }
          if (content.nextStepOnError?.id === node.id) {
            content.nextStepOnError = null;
          } else if (
            content.nextStepOnError &&
            !visitedNodeIds.has(content.nextStepOnError.id)
          ) {
            nodes.push(content.nextStepOnError);
          }
        }

        if (content instanceof ResponseCardsTreeNode) {
          content.items.forEach((item) => {
            if (item.nextStep?.id === node.id) {
              item.nextStep = null;
              item.stepNumber = null;
            } else if (item.nextStep && !visitedNodeIds.has(item.nextStep.id)) {
              nodes.push(item.nextStep);
            }
          });
        }

        if (content instanceof ConditionalTreeNode) {
          if (content.trueStep) {
            if (content.trueStep.id === node.id) {
              content.trueStep = null;
            } else {
              traverseAndDelete(content.trueStep);
            }
          }
          if (content.falseStep) {
            if (content.falseStep.id === node.id) {
              content.falseStep = null;
            } else {
              traverseAndDelete(content.falseStep);
            }
          }
        }

        if (content instanceof CollectVariableTreeNode) {
          if (content.fallbackStep) {
            if (content.fallbackStep.id === node.id) {
              content.fallbackStep = null;
            } else if (!visitedNodeIds.has(content.fallbackStep.id)) {
              nodes.push(content.fallbackStep);
            }
          }
          if (content.slotProvidedStep) {
            if (content.slotProvidedStep.id === node.id) {
              content.slotProvidedStep = null;
            } else if (!visitedNodeIds.has(content.slotProvidedStep.id)) {
              nodes.push(content.slotProvidedStep);
            }
          }
          if (content.slotConditionals) {
            content.slotConditionals.forEach((cond) => {
              if (cond.nextStep) {
                if (cond.nextStep.id === node.id) {
                  cond.nextStep = null;
                } else if (!visitedNodeIds.has(cond.nextStep.id)) {
                  nodes.push(cond.nextStep);
                }
              }
            });
          }
        }
      };

      while (nodes.length > 0) {
        const currentNode = nodes.shift()!;
        // eslint-disable-next-line no-continue
        if (visitedNodeIds.has(currentNode.id)) continue;
        visitedNodeIds.add(currentNode.id);
        traverseAndDelete(currentNode);
      }

      setUnconnectedNodes((prev) => prev.filter((n) => n.id !== node.id));

      const newSlotsRebuilt: IntentSlot[] = [];
      const visitedNodes = new Set<string>();
      const rebuildSlots = (currentNode: BotResponseTreeNode): void => {
        if (visitedNodes.has(currentNode.id)) {
          return;
        }
        visitedNodes.add(currentNode.id);

        const { content } = currentNode;
        if (content instanceof CollectVariableTreeNode) {
          if (content.slotName && content.slotType) {
            newSlotsRebuilt.push({
              id: content.intentSlotId,
              slotName: content.slotName,
              slotType: content.slotType
            });
          } else if (
            content.slotName &&
            !slotExistsByName(newSlotsRebuilt, content.slotName)
          ) {
            newSlotsRebuilt.push({
              id: null,
              slotName: content.slotName,
              slotType: null,
              inlineValueType: content.slotValueType ?? undefined
            });
          }
        }
        if (content instanceof CodeBlockTreeNode) {
          if (content.outputMappings) {
            content.outputMappings
              .filter((mapping) => mapping.intentVariable)
              .filter(
                (mapping) =>
                  !slotExistsByName(newSlotsRebuilt, mapping.intentVariable)
              )
              .forEach((mapping) => {
                newSlotsRebuilt.push({
                  id: null,
                  slotName: mapping.intentVariable,
                  slotType: null,
                  inlineValueType: SlotValueType.FREE_TEXT
                });
              });
          }
        }
        if (content instanceof LlmTreeNode) {
          if (content.outputVariableName) {
            const existingSlotIndex = newSlotsRebuilt.findIndex(
              (slot) => slot.slotName === content.outputVariableName
            );

            if (existingSlotIndex !== -1) {
              newSlotsRebuilt[existingSlotIndex] = {
                ...newSlotsRebuilt[existingSlotIndex],
                inlineValueType:
                  content.outputValueType ?? SlotValueType.FREE_TEXT
              };
            } else {
              newSlotsRebuilt.push({
                id: null,
                slotName: content.outputVariableName,
                slotType: null,
                inlineValueType:
                  content.outputValueType ?? SlotValueType.FREE_TEXT
              });
            }
          }
        }
        if (
          content instanceof IntentTreeNode ||
          content instanceof MessageBlockTreeNode ||
          content instanceof GenerativeAiTreeNode ||
          content instanceof CalculationTreeNode ||
          content instanceof SendEmailTreeNode ||
          content instanceof SendSmsTreeNode ||
          content instanceof DeleteVariableTreeNode ||
          content instanceof LlmTreeNode
        ) {
          if (content.nextStep) rebuildSlots(content.nextStep);
        }
        if (content instanceof CodeBlockTreeNode) {
          if (content.nextStep) rebuildSlots(content.nextStep);
          if (content.nextStepOnError) rebuildSlots(content.nextStepOnError);
        }
        if (content instanceof ResponseCardsTreeNode) {
          content.items.forEach((item) => {
            if (item.nextStep) rebuildSlots(item.nextStep);
          });
        }
        if (content instanceof ConditionalTreeNode) {
          if (content.trueStep) rebuildSlots(content.trueStep);
          if (content.falseStep) rebuildSlots(content.falseStep);
        }
        if (content instanceof CollectVariableTreeNode) {
          if (content.fallbackStep) rebuildSlots(content.fallbackStep);
          if (content.slotProvidedStep) rebuildSlots(content.slotProvidedStep);
          if (content.slotConditionals) {
            content.slotConditionals.forEach((cond) => {
              if (cond.nextStep) rebuildSlots(cond.nextStep);
            });
          }
        }
      };

      rebuildSlots(updatedIntent.rootNode);
      unconnectedNodes.forEach((unconnectedNode) => {
        if (unconnectedNode.id !== node.id) {
          rebuildSlots(unconnectedNode);
        }
      });
      updatedIntent.slots = newSlotsRebuilt;
      setIntent(updatedIntent);
      setIntentHasChanged(true);
    },
    [intent, unconnectedNodes]
  );

  useEffect(() => {
    refreshSlots();
  }, [refreshSlots]);

  useEffect(() => {
    if (user?.mfaComplete) {
      SystemVariableService.getSystemVariables().then(setSystemVariables);
    }
  }, [user?.id, user?.mfaComplete]);

  const intentSlotMap = useMemo(() => {
    if (!intent) return {};
    return intent.slots.reduce(
      (map, intentSlot) => {
        const matchingSlot = slots.find(
          (slot) => slot.slotName === intentSlot.slotType
        );
        if (matchingSlot) {
          map[intentSlot.slotName] = {
            ...matchingSlot,
            intentSlotId: intentSlot.id
          };
        }
        return map;
      },
      {} as Record<string, any>
    );
  }, [intent, slots]);

  const startInitializing = useCallback((intentId: number) => {
    isInitializingRef.current = true;
    initializingIntentIdRef.current = intentId;
    initializationTimestampRef.current = Date.now();
  }, []);

  const finishInitializing = useCallback(() => {
    isInitializingRef.current = false;
  }, []);

  const value = useMemo<BotBuilderContextProps>(
    () => ({
      intent,
      setIntent,
      intentHasChanged,
      setIntentHasChanged,
      slots,
      setSlots,
      refreshSlots,
      updateSlot,
      deleteSlot,
      createSlot,
      onNodeUpdate,
      onNodeDelete,
      addNode,
      unconnectedNodes,
      setUnconnectedNodes,
      removeNode,
      intentSlotMap,
      systemVariables,
      setSystemVariables,
      startInitializing,
      finishInitializing
    }),
    [
      intent,
      intentHasChanged,
      slots,
      refreshSlots,
      updateSlot,
      deleteSlot,
      createSlot,
      onNodeUpdate,
      onNodeDelete,
      addNode,
      unconnectedNodes,
      setUnconnectedNodes,
      removeNode,
      intentSlotMap,
      systemVariables,
      startInitializing,
      finishInitializing
    ]
  );

  return (
    <BotBuilderContext.Provider value={value}>
      {children}
    </BotBuilderContext.Provider>
  );
}

export default BotBuilderProvider;
